import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PostsService, 
  CampaignsService, 
  AnalyticsService,
  OrganizationsService,
  ProfilesService,
  LinksService,
  ReferralsService,
  SocialAccountsService
} from '@/services';
import type { PostFilters } from '@/services/posts.service';
import type { Database } from '@/integrations/supabase/types';
import type { Post } from '@/types/posts';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/query-config';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// Profile Hooks
export const useProfile = (userId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.profiles.detail(userId || ''),
    queryFn: () => ProfilesService.getById(userId!),
    enabled: !!userId,
    staleTime: 0, // Always refetch - critical for routing decisions
    refetchOnMount: 'always', // Ensure fresh data on mount
  });
};

export const useUserOrganization = (userId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.profiles.organization(userId || ''),
    queryFn: () => ProfilesService.getUserOrganization(userId!),
    enabled: !!userId,
  });
};

type AppRole = Database['public']['Enums']['app_role'];

interface UseUserRoleReturn {
  role: AppRole | null;
  loading: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  canManageDNA: boolean;
  canEditContent: boolean;
  refetch: () => void;
}

/**
 * Hook to get the current user's role in their organization
 * Supports both context-based (no args) and explicit args usage
 * 
 * Usage:
 *   const { isAdmin, canManageDNA } = useUserRole(); // uses AuthContext
 *   const { isAdmin } = useUserRole(userId, orgId);  // explicit params
 */
export const useUserRole = (explicitUserId?: string, explicitOrgId?: string): UseUserRoleReturn => {
  const { user, organization } = useAuth();
  
  // Use explicit params if provided, otherwise fall back to context
  const userId = explicitUserId ?? user?.id;
  const organizationId = explicitOrgId ?? organization?.id;

  const query = useQuery({
    queryKey: queryKeys.profiles.role(userId || '', organizationId || ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId!)
        .eq('organization_id', organizationId!)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!organizationId,
  });

  const role = query.data?.role as AppRole | null ?? null;

  return {
    role,
    loading: query.isLoading,
    isOwner: role === 'owner',
    isAdmin: role === 'admin' || role === 'owner',
    isEditor: role === 'editor',
    isViewer: role === 'viewer',
    canManageDNA: role === 'owner' || role === 'admin',
    canEditContent: role === 'owner' || role === 'admin' || role === 'editor',
    refetch: query.refetch,
  };
};

// Post Hooks
export const usePosts = (filters: PostFilters) => {
  return useQuery({
    queryKey: queryKeys.posts.list({ ...filters } as Record<string, unknown>),
    queryFn: () => PostsService.getAll(filters),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!filters.organizationId, // Prevent query when organizationId is empty
  });
};

export const usePost = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.posts.detail(id || ''),
    queryFn: () => PostsService.getById(id!),
    enabled: !!id,
  });
};

export const useScheduledPosts = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.posts.scheduled(organizationId || ''),
    queryFn: () => PostsService.getScheduled(organizationId!),
    enabled: !!organizationId,
    refetchInterval: 60 * 1000, // Refetch every minute
  });
};

export const useCreatePost = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (post: Database['public']['Tables']['posts']['Insert']) => {
      return PostsService.create(post);
    },
    onSuccess: (newPost) => {
      // Granular invalidation - only invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.posts.list({ organizationId: newPost.organization_id })
      });
      if (newPost.scheduled_for) {
        const date = new Date(newPost.scheduled_for);
        queryClient.invalidateQueries({
          queryKey: queryKeys.posts.byMonth(newPost.organization_id, date.getFullYear(), date.getMonth())
        });
      }
      toast({
        title: "Post created",
        description: "Your post has been created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating post",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdatePost = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Database['public']['Tables']['posts']['Update']> }) => {
      return PostsService.update(id, updates);
    },
    onSuccess: (updatedPost, { id }) => {
      // Direct cache update + targeted invalidation
      queryClient.setQueryData(queryKeys.posts.detail(id), updatedPost);
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.posts.list({ organizationId: updatedPost.organization_id })
      });
      toast({
        title: "Post updated",
        description: "Your post has been updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating post",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (postId: string) => {
      return PostsService.delete(postId);
    },
    onMutate: async (postId) => {
      // Optimistically remove from cache
      await queryClient.cancelQueries({ queryKey: queryKeys.posts.all });
      queryClient.setQueriesData<Post[]>(
        { queryKey: queryKeys.posts.lists() },
        (old) => old?.filter(post => post.id !== postId)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.lists() });
      toast({
        title: "Post deleted",
        description: "Your post has been deleted successfully",
      });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      toast({
        title: "Error deleting post",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useBulkDeletePosts = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (postIds: string[]) => {
      return PostsService.bulkDelete(postIds);
    },
    onMutate: async (postIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.posts.all });
      queryClient.setQueriesData<Post[]>(
        { queryKey: queryKeys.posts.lists() },
        (old) => old?.filter(post => !postIds.includes(post.id))
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.lists() });
      const skippedCount = result.skipped.campaignPosts.length + result.skipped.publishedPosts.length;
      
      if (result.deleted.length > 0) {
        toast({
          title: `${result.deleted.length} post${result.deleted.length !== 1 ? 's' : ''} deleted`,
          description: skippedCount > 0 
            ? `${skippedCount} skipped (campaign or published)` 
            : undefined,
        });
      } else {
        toast({
          title: "No posts deleted",
          description: "Selected posts are either in campaigns or already published",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      toast({
        title: "Error deleting posts",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useBulkUpdatePostStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ postIds, newStatus }: { postIds: string[]; newStatus: 'draft' | 'scheduled' }) => {
      return PostsService.bulkUpdateStatus(postIds, newStatus);
    },
    onSuccess: (result, { newStatus }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.lists() });
      const skippedCount = result.skipped.campaignPosts.length + result.skipped.publishedPosts.length;
      
      if (result.updated.length > 0) {
        toast({
          title: `${result.updated.length} post${result.updated.length !== 1 ? 's' : ''} updated`,
          description: `Status changed to ${newStatus}${skippedCount > 0 ? `. ${skippedCount} skipped.` : ''}`,
        });
      } else {
        toast({
          title: "No posts updated",
          description: "Selected posts cannot be modified",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      toast({
        title: "Error updating posts",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Campaign Hooks
export const useCampaigns = (
  organizationId: string | undefined, 
  options?: { limit?: number; offset?: number }
) => {
  return useQuery({
    queryKey: [...queryKeys.campaigns.list(organizationId || ''), options],
    queryFn: async () => {
      const result = await CampaignsService.getAll(organizationId!, options);
      return result.data; // Return just the data array for backward compatibility
    },
    enabled: !!organizationId,
  });
};

export const useCampaign = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.campaigns.detail(id || ''),
    queryFn: () => CampaignsService.getById(id!),
    enabled: !!id,
  });
};

export const useCampaignTemplates = (organizationId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.campaigns.templates(), organizationId],
    queryFn: () => CampaignsService.getTemplates(organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (campaign: Database['public']['Tables']['campaigns']['Insert']) => {
      return CampaignsService.create(campaign);
    },
    onSuccess: (newCampaign) => {
      // Granular invalidation for specific org
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.campaigns.list(newCampaign.organization_id)
      });
      toast({
        title: "Campaign created",
        description: "Your campaign has been created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating campaign",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Database['public']['Tables']['campaigns']['Update']> }) =>
      CampaignsService.update(id, updates),
    onSuccess: (updatedCampaign, { id }) => {
      // Direct update + targeted invalidation
      queryClient.setQueryData(queryKeys.campaigns.detail(id), updatedCampaign);
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.campaigns.list(updatedCampaign.organization_id)
      });
      toast({
        title: "Campaign updated",
        description: "Your campaign has been updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating campaign",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Brand hooks removed - using organization-only model for MVP

// Organization Hooks
export const useOrganizations = () => {
  return useQuery({
    queryKey: queryKeys.organizations.lists(),
    queryFn: () => OrganizationsService.getAll(),
  });
};

export const useOrganization = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.organizations.detail(id || ''),
    queryFn: () => OrganizationsService.getById(id!),
    enabled: !!id,
  });
};

export const useOrganizationSubscription = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.organizations.subscription(organizationId || ''),
    queryFn: () => OrganizationsService.getSubscription(organizationId!),
    enabled: !!organizationId,
  });
};

// Analytics Hooks
export const useAnalyticsSummary = (
  organizationId: string | undefined,
  timeRange?: { startDate: string; endDate: string }
) => {
  return useQuery({
    queryKey: queryKeys.analytics.summary(
      organizationId || '',
      timeRange?.startDate,
      timeRange?.endDate
    ),
    queryFn: () => AnalyticsService.getPostSummary(organizationId!, timeRange),
    enabled: !!organizationId,
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useLinkAnalytics = (linkId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.analytics.linkAnalytics(linkId || ''),
    queryFn: () => AnalyticsService.getLinkAnalytics(linkId!),
    enabled: !!linkId,
  });
};

export const useBestPostingTimes = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.analytics.bestTimes(organizationId || ''),
    queryFn: () => AnalyticsService.getBestPostingTimes(organizationId!),
    enabled: !!organizationId,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};

export const usePerformanceInsights = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.analytics.insights(organizationId || ''),
    queryFn: () => AnalyticsService.getPerformanceInsights(organizationId!),
    enabled: !!organizationId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

// Link Hooks
export const useLinks = (
  organizationId: string | undefined,
  options?: { limit?: number; offset?: number }
) => {
  return useQuery({
    queryKey: [...queryKeys.links.list(organizationId || ''), options],
    queryFn: async () => {
      const result = await LinksService.getAll(organizationId!, options);
      return result.data;
    },
    enabled: !!organizationId,
  });
};

export const useLink = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.links.detail(id || ''),
    queryFn: () => LinksService.getById(id!),
    enabled: !!id,
  });
};

export const useLinkAnalyticsData = (linkId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.links.analytics(linkId || ''),
    queryFn: () => LinksService.getAnalytics(linkId!),
    enabled: !!linkId,
  });
};

export const useDeleteLink = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: LinksService.delete,
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.links.all });
      
      type LinksData = { data: Array<{ id: string; [key: string]: unknown }>; count: number };
      
      queryClient.setQueriesData<LinksData>(
        { queryKey: queryKeys.links.all },
        (old) => ({ 
          data: old?.data?.filter((link) => link.id !== linkId) || [],
          count: (old?.count || 1) - 1,
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.links.all });
      toast({
        title: "Link deleted",
        description: "The short link has been removed",
      });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.links.all });
      toast({
        title: "Error deleting link",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// A/B Test Hooks removed - feature hidden for MVP

// Referral Hooks
export const useReferralCode = (userId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.referrals.code(userId || ''),
    queryFn: () => ReferralsService.getUserReferralCode(userId!),
    enabled: !!userId,
  });
};

export const useReferrals = (userId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.referrals.byUser(userId || ''),
    queryFn: () => ReferralsService.getUserReferrals(userId!),
    enabled: !!userId,
  });
};

export const useReferralStats = (userId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.referrals.stats(userId || ''),
    queryFn: () => ReferralsService.getUserStats(userId!),
    enabled: !!userId,
  });
};

export const useRewards = (userId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.referrals.rewards(userId || ''),
    queryFn: () => ReferralsService.getUserRewards(userId!),
    enabled: !!userId,
  });
};

// Connection Health Hooks
export const useConnectionHealth = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['connection-health', userId],
    queryFn: () => SocialAccountsService.getHealthStatus(userId!),
    enabled: !!userId,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useCheckConnectionHealth = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => SocialAccountsService.checkHealth(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['connection-health', userId] });
      toast({
        title: 'Health check complete',
        description: 'All connections have been checked',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Health check failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
